import React, { useCallback, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styled from "styled-components";
import { Alert } from "react-bootstrap";
import { useServices, useAsyncCommand, useModuleSettingProduct } from "@hooks";
import {
	ActionButton,
	Button,
	ButtonContainer,
	Form,
	QueryError,
} from "@components";
import {
	NewModuleSetting,
	RootProductId,
	UpdatedModuleSetting,
	User,
} from "@models";
import { defer } from "@util/helpers";
import ProductsPageContext from "../ProductsPageContext";
import AvailabilityDates from "./configuration/AvailabilityDates";
import ModuleSettingMobilitySettings from "./configuration/ModuleSettingMobilitySettings";
import { ConfigModuleSetting } from "./configuration/types";
import {
	canConfigureBalanceAccount,
	configToModuleSetting,
	getBalanceAccount,
	useDefaultConfigModuleSetting,
	isNewModuleSetting,
} from "./util";
import { FormGrid, FormSection, FormSectionContent } from "@components";
import { checkCollidingModuleSettings } from "./util/checkCollidingModuleSettings";
import CouponConfigurationSelection from "./congrats/CouponConfigurationSelection";
import BalanceAccountConfiguration from "./balanceAccounts/BalanceAccountConfiguration";
import UserDetailsPageContext from "../../user/UserDetailsPageContext";
import MobilityVariantToggle from "./configuration/MobilityVariantToggle";
import BudgetCategorySection from "./configuration/BudgetCategorySection";
import _ from "lodash";
import ModuleSettingTravelSettings from "./configuration/ModuleSettingTravelSettings";
import ExcessBudgetSettings from "./ExcessBudgetSettings";
import BugetCorrectionSetting from "./BudgetCorrectionSetting";

type Props = {
	moduleSetting: UpdatedModuleSetting | NewModuleSetting;
	users?: User[];
	onClose?: (needsConfirmation: boolean) => void;
	onFormDirty?: (dirty: boolean) => void;
};

export default function ModuleSettingConfiguration(props: Props) {
	const { moduleSetting, users, onFormDirty } = props;
	const [collisionError, setCollisionError] = useState(false);
	const [collisionMessage, setCollisionMessage] = useState<string | undefined>(
		undefined
	);
	const [alertVariant, setAlertVariant] = useState<"warning" | "danger">(
		"warning"
	);

	const { product } = useModuleSettingProduct(moduleSetting);

	const formMethods = useForm<ConfigModuleSetting>({
		defaultValues: useDefaultConfigModuleSetting(moduleSetting) as any,
		mode: "onChange",
		shouldFocusError: false,
	});

	const { productService } = useServices();

	const pageContext = useContext(ProductsPageContext);

	const isNew = isNewModuleSetting(moduleSetting);
	const isMobility = moduleSetting.category === "mobility";
	const isTravel = moduleSetting.category.startsWith("travel");
	const hasBudgets = product.hasBudgets();

	const { formState, handleSubmit, watch } = formMethods;
	const productVariantId = watch("productVariantId");
	const availableFrom = watch("availableFrom");
	const availableUntil = watch("availableUntil");

	const variant = product.variantOfId(productVariantId);
	const currentBudgetCategories = variant.budgetCategories;
	const hasSubBudgets = currentBudgetCategories.length > 1;

	const allBudgetCategories = _.flatMap(
		product.variants,
		(v) => v.budgetCategories
	);

	const [triggerSave, saveQuery] = useAsyncCommand(
		(ms: UpdatedModuleSetting | NewModuleSetting, users?: User[]) => {
			if (isNewModuleSetting(ms)) {
				let newMSs = [ms];
				if (users) {
					// copy ModuleSetting, user for all selected users
					newMSs = users.map((u) => ({ ...ms, userId: u.userId }));
				}
				return productService.createNewModuleSetting(newMSs);
			} else {
				return productService.updateModuleSetting(ms);
			}
		},
		{
			rethrowError: false,
		}
	);

	const handleChangeAvailableDates = useCallback(() => {
		setCollisionMessage(undefined);
		setCollisionError(false);

		const moduleSettings = pageContext.moduleSettings;
		const { isValid, message, isWarning } = checkCollidingModuleSettings(
			moduleSettings,
			moduleSetting,
			availableFrom,
			availableUntil
		);
		if (!isValid && saveQuery.state !== "success") {
			setCollisionMessage(message);
			setCollisionError(!isWarning);
			setAlertVariant(isWarning ? "warning" : "danger");
		}
	}, [
		saveQuery,
		availableFrom,
		availableUntil,
		moduleSetting,
		pageContext.moduleSettings,
	]);

	useEffect(() => {
		// note: this is quite ugly, but we want to make sure to prevent accidentally closing the card if the form is dirty
		onFormDirty?.(formState.isDirty);
	}, [onFormDirty, formState.isDirty]);

	useEffect(() => {
		if (
			moduleSetting.category !== "congrats" &&
			(formState.dirtyFields.availableFrom ||
				formState.dirtyFields.availableUntil ||
				isNew)
		) {
			handleChangeAvailableDates();
		}
	}, [
		formState.dirtyFields.availableFrom,
		formState.dirtyFields.availableUntil,
		isNew,
		handleChangeAvailableDates,
		moduleSetting.category,
	]);

	const handleSubmitForm = handleSubmit(async (formValues) => {
		const updatedValues = configToModuleSetting(
			formValues,
			moduleSetting,
			product
		);

		const state = await triggerSave(updatedValues, users);
		if (state.state === "success") {
			pageContext.triggerRefresh();

			await defer(() => {
				props.onClose?.(false);
			});
		}
	});

	// formState.isValid is not turning to true once it became false when all categories are unselected, so formState.errors is used instead
	const isSaveButtonDisabled =
		!!Object.keys(formState.errors).length ||
		!(formState.isDirty || isNew) ||
		collisionError;

	const { user } = useContext(UserDetailsPageContext);

	const canUseBalanceAccount = () => {
		if (!isNew) {
			const hasBalanceAccount = getBalanceAccount(user, moduleSetting);
			const ms: UpdatedModuleSetting = moduleSetting; // WORKAROUND: moduleSetting is narrowed to `never` for some reason
			return (
				canConfigureBalanceAccount(ms.category as RootProductId) &&
				hasBalanceAccount
			);
		} else {
			return canConfigureBalanceAccount(
				moduleSetting.category as RootProductId
			);
		}
	};

	return (
		<Container data-testid="moduleSetting-config-container">
			<QueryError queryState={saveQuery} />

			<FormProvider {...formMethods}>
				<Form onSubmit={handleSubmitForm}>
					{moduleSetting.category === "congrats" ? (
						<CouponConfigurationSelection moduleSetting={moduleSetting} />
					) : (
						<>
							{!!collisionMessage && (
								<StyledFormSectionContent>
									<Alert variant={alertVariant}>{collisionMessage}</Alert>
								</StyledFormSectionContent>
							)}
							<FormGrid>
								{/* general settings */}
								<FormSection>Einstellungen</FormSection>
								<StyledFormSectionContent>
									<AvailabilityDates />
									<ExcessBudgetSettings />
									<BugetCorrectionSetting />
									{isMobility && (
										<ModuleSettingMobilitySettings
											moduleSetting={moduleSetting}
										/>
									)}
									{isTravel && <ModuleSettingTravelSettings />}
								</StyledFormSectionContent>
								{/* Balance Account configuration */}
								{canUseBalanceAccount() && (
									<BalanceAccountConfiguration
										moduleSetting={moduleSetting}
										isNewModuleSetting={isNew}
									/>
								)}
								{/* product variant */}
								{isMobility && (
									<>
										<FormSection>Variante</FormSection>

										<MobilityVariantToggle />
									</>
								)}

								{/* budget configuration */}
								{hasBudgets && (
									<>
										<FormSection>
											{hasSubBudgets ? "Budgets" : "Budget"}
										</FormSection>

										<FormSectionContent>
											{allBudgetCategories.map((bcat) => (
												<BudgetCategorySection
													key={bcat.id}
													product={product}
													category={bcat}
												/>
											))}
										</FormSectionContent>
									</>
								)}
							</FormGrid>
						</>
					)}
					{/* form buttons */}
					<ButtonContainer>
						<Button
							variant="outline-secondary"
							onClick={() => props.onClose?.(true)}
						>
							Abbrechen
						</Button>
						<ActionButton
							type="submit"
							disabled={isSaveButtonDisabled}
							query={saveQuery}
							variant={isNew ? undefined : "warning"}
							successContent={"Gespeichert"}
						>
							{isNew ? "Speichern" : "Überschreiben"}
						</ActionButton>
					</ButtonContainer>
				</Form>
			</FormProvider>
		</Container>
	);
}

const Container = styled.div`
	padding: 16px;
`;

const StyledFormSectionContent = styled(FormSectionContent)`
	grid-column: span 2;
	margin-top: 8px;
	display: block;
`;
